import { type Theme } from '@emotion/react';
import { color } from './Styles';

export const emotionLightTheme: Theme = {
    mode: 'light',
    panelPosition: 'right',
    panelOrientation: 'vertical',
    spacing: 'comfortable',
    scaleSize: 16,
    panelButtonScaleSize: 18,
    color: {
        background: color.bg_light,
        card: color.neutral_accent_2,
        cardHover: color.secondary_medium_light,
        accentCard: color.neutral_accent_2,
        border: color.secondary_card_border,
        text: color.primary_dark,
        textBody: color.neutral,
        textBodyLight: color.accent_dark,
        error: color.error,
        neutral: color.border,
        link: color.secondary,
        accent: color.secondary,
        tint: color.accent,
        danger: color.error,
        warning: color.accent_2,
        success: color.success,
        secondary: color.secondary,
        sidebarDivider: color.secondary_card_border,
        indicator: color.secondary,
        waveVoice: color.accent_dark,
        waveDictation: color.tertiary,
        remoteDevice: color.accent_2_dark,
        remoteDeviceText: color.primary_dark,
        licenseHighlight: color.accent_2_dark,
        control: color.accent,
        create: color.secondary,
        automate: color.accent_2_dark,
    },
    input: {
        text: '#1A1C1E',
        placeholder: '#54606F',
        border: color.border,
        background: color.neutral_primary,
    },
    dialog: {
        background: color.bg_light,
        text: color.primary_intense,
        warn: color.accent_2,
        success: color.success_light,
        remote: color.accent,
        border: color.primary_intense,
    },
    tag: {
        background: color.accent_2,
        text: color.primary_dark,
    },
    banner: {
        default: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.primary_dark,
            border: color.primary_dark,
            linkText: color.primary_dark,
            linkBackground: color.primary_dark,
            linkHoverText: color.accent_2,
            linkHoverBackground: `#fdf3db`,
        },
        accent: {
            text: color.secondary,
            buttonText: color.bg_light,
            background: color.secondary_medium_light,
            icon: color.secondary,
            border: color.secondary,
            linkText: color.secondary,
            linkBackground: color.secondary_medium_light,
            linkHoverText: color.secondary,
            linkHoverBackground: color.secondary_medium_light,
        },
        error: {
            text: color.bg_light,
            background: color.bg_light,
            icon: color.error,
            border: color.error,
            linkText: color.error,
            linkBackground: `${color.error}15`,
            linkHoverText: color.error,
            linkHoverBackground: `${color.error}15`,
        },
        warning: {
            text: color.primary_dark,
            background: `${color.accent_2}15`,
            icon: color.primary_dark,
            border: color.accent_2,
            linkText: color.primary_dark,
            linkBackground: color.accent_2,
            linkHoverText: color.accent_2,
            linkHoverBackground: `${color.accent_2}15`,
        },
        success: {
            text: color.bg_light,
            background: `${color.success}15`,
            icon: color.success,
            border: color.success,
            linkText: color.success,
            linkBackground: `${color.success}15`,
            linkHoverText: color.success,
            linkHoverBackground: `${color.success}15`,
        },
        info: {
            text: color.primary_dark,
            background: `${color.accent}15`,
            icon: color.accent,
            border: color.accent,
            linkText: color.primary_dark,
            linkBackground: `${color.neutral_accent_1}15`,
            linkHoverText: color.neutral_accent_1,
            linkHoverBackground: `${color.neutral_accent_1}15`,
        },
    },
    button: {
        primary: {
            text: color.neutral_accent_1,
            background: color.secondary,
            icon: color.secondary,
            border: color.secondary_medium,
            hoverText: color.secondary,
            hoverBackground: color.secondary_card_border,
        },
        secondary: {
            text: color.secondary,
            background: color.secondary_card_border,
            icon: color.secondary,
            border: color.secondary,
            hoverText: color.secondary_card_border,
            hoverBackground: color.secondary,
        },
        accent: {
            text: color.primary_dark,
            background: color.bg_light,
            icon: color.secondary,
            border: color.secondary,
            hoverText: color.secondary,
            hoverBackground: color.neutral_accent_2,
        },
        simple: {
            text: color.neutral,
            background: color.bg_light,
            icon: color.neutral,
            border: 'none',
            hoverText: color.primary_dark,
            hoverBackground: color.bg_light,
        },
        quick: {
            text: color.neutral,
            background: color.bg_light,
            icon: color.tertiary,
            border: color.neutral,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
        disabled: {
            text: color.neutral,
            background: color.neutral_accent_1,
            icon: color.neutral,
            border: color.neutral_light,
            hoverText: color.neutral,
            hoverBackground: color.neutral_accent_1,
        },
        // Round shell/panel control: card surface, accent icon (mirrors color.card/accent/border/cardHover).
        footer: {
            text: color.secondary,
            background: color.neutral_accent_2,
            icon: color.secondary,
            border: color.secondary_card_border,
            hoverText: color.secondary,
            hoverBackground: color.secondary_medium_light,
        },
        // Accent-filled primary action (e.g. send).
        send: {
            text: color.bg_light,
            background: color.secondary,
            icon: color.bg_light,
            border: color.secondary,
            hoverText: color.bg_light,
            hoverBackground: color.secondary,
        },
        // Destructive action: error-red, fills red with white content on hover.
        danger: {
            text: color.error,
            background: 'transparent',
            icon: color.error,
            border: color.error,
            hoverText: color.bg_light,
            hoverBackground: color.error,
        },
    },
};

export const emotionDarkTheme: Theme = {
    mode: 'dark',
    panelPosition: 'right',
    panelOrientation: 'vertical',
    spacing: 'comfortable',
    scaleSize: 16,
    panelButtonScaleSize: 18,
    color: {
        // background: color.primary_intense,

        background: color.primary_intense, // TODO: update to actual dark mode background
        card: color.bg_dark,
        cardHover: color.primary_light,
        accentCard: color.primary_dark,
        border: color.primary_dark,
        text: color.bg_light,
        textBody: color.border,
        textBodyLight: color.accent,
        error: color.error_light,
        neutral: color.neutral,
        link: color.accent_2,
        accent: color.accent_light,
        tint: color.accent_light,
        danger: color.tertiary,
        warning: color.accent_2,
        success: color.success_light,
        secondary: color.secondary_light,
        sidebarDivider: color.secondary_light + '20',
        indicator: color.accent_light,
        waveVoice: color.accent_light,
        waveDictation: color.accent_2,
        remoteDevice: color.accent_2,
        remoteDeviceText: color.primary_dark,
        licenseHighlight: color.secondary_light,
        control: color.accent,
        create: color.secondary_light,
        automate: color.accent_2_light,
    },

    input: {
        text: color.bg_light, // onSurface
        placeholder: color.accent_dark, // surfaceVariant
        border: color.neutral_dark, // outlineVariant
        background: color.neutral_dark, // onSurfaceVariant
    },
    dialog: {
        background: color.bg_light,
        text: color.primary_intense,
        warn: color.accent_2,
        success: color.success_light,
        remote: color.accent,
        border: color.primary_intense,
    },
    tag: {
        background: color.secondary_light,
        text: color.primary_dark,
    },
    banner: {
        default: {
            text: color.bg_light,
            background: color.primary_dark,
            icon: color.bg_light,
            border: color.primary_dark,
            linkText: color.primary_dark,
            linkBackground: color.primary_dark,
            linkHoverText: color.accent_2,
            linkHoverBackground: `#fdf3db`,
        },
        accent: {
            text: color.secondary_card_border,
            body: color.secondary_card_border,
            buttonText: '#20214d',
            background: `${color.secondary}70`,
            icon: color.secondary_light,
            border: color.secondary_light,
            linkText: color.secondary_medium_light,
            linkBackground: color.secondary_medium,
            linkHoverText: color.bg_light,
            linkHoverBackground: color.secondary_medium,
        },
        error: {
            text: color.bg_light,
            background: color.bg_dark,
            icon: color.tertiary,
            border: color.tertiary,
            linkText: color.tertiary,
            linkBackground: color.bg_dark,
            linkHoverText: color.tertiary,
            linkHoverBackground: color.bg_dark,
            buttonText: color.bg_dark,
        },
        warning: {
            text: color.bg_light,
            background: `${color.accent_2}25`,
            icon: color.bg_light,
            border: color.accent_2,
            linkText: color.accent_2,
            linkBackground: color.accent_2,
            linkHoverText: color.bg_light,
            linkHoverBackground: `${color.accent_2}15`,
            buttonText: color.primary_dark,
        },
        success: {
            text: color.bg_light,
            background: `${color.success}15`,
            icon: color.success,
            border: color.success,
            linkText: color.bg_light,
            linkBackground: `${color.success}15`,
            linkHoverText: color.success,
            linkHoverBackground: `${color.success}15`,
        },
        info: {
            text: color.accent_card_border,
            buttonText: color.primary_intense,
            background: `${color.accent}15`,
            icon: color.accent,
            border: color.accent,
            linkText: color.bg_light,
            linkBackground: `${color.neutral_accent_1}15`,
            linkHoverText: color.neutral_accent_1,
            linkHoverBackground: `${color.neutral_accent_1}15`,
        },
    },
    button: {
        primary: {
            text: color.primary_dark,
            background: color.accent_2,
            icon: color.accent_2,
            border: color.accent_2,
            hoverText: color.accent_2,
            hoverBackground: 'transparent',
        },
        secondary: {
            text: color.accent_light,
            background: color.primary_dark,
            icon: color.primary_dark,
            border: color.accent,
            hoverText: color.primary_dark,
            hoverBackground: color.accent_light,
        },
        accent: {
            text: color.border,
            background: color.bg_dark,
            icon: color.accent_2,
            border: color.accent_2,
            hoverText: color.accent_2,
            hoverBackground: color.primary_intense,
        },
        simple: {
            text: color.border,
            background: 'transparent',
            icon: color.border,
            border: 'none',
            hoverText: color.accent_2,
            hoverBackground: color.bg_dark,
        },
        quick: {
            text: color.bg_light,
            background: color.bg_dark,
            icon: color.tertiary,
            border: color.secondary_light,
            hoverText: color.bg_light,
            hoverBackground: color.primary_dark,
        },
        disabled: {
            text: color.neutral_light,
            background: color.neutral_dark,
            icon: color.neutral_light,
            border: color.neutral_medium,
            hoverText: color.neutral_light,
            hoverBackground: color.neutral_dark,
        },
        // Round shell/panel control: card surface, accent icon (mirrors color.card/accent/border/cardHover).
        footer: {
            text: color.accent_light,
            background: color.primary_intense,
            icon: color.accent_light,
            border: color.primary_light,
            hoverText: color.accent_light,
            hoverBackground: color.primary_light,
        },
        // Accent-filled primary action (e.g. send).
        send: {
            text: color.bg_dark,
            background: color.accent_light,
            icon: color.bg_dark,
            border: color.accent_light,
            hoverText: color.bg_dark,
            hoverBackground: color.accent_light,
        },
        // Destructive action: error-red, fills red with white content on hover.
        danger: {
            text: color.error_light,
            background: 'transparent',
            icon: color.error_light,
            border: color.error_light,
            hoverText: color.bg_light,
            hoverBackground: color.error_light,
        },
    },
};
